import React, { useEffect, useRef, useState } from "react";
import { createDiscountCode, deleteDiscountCode, editDiscountCode, getDiscountCodes } from "../api/requests/discountCodes";
import { Input } from "../components/form/Input";
import { NumberInput } from "../components/form/NumberInput";
import Checkbox from "../components/form/Checkbox";
import { DiscountCodeModel } from "../api/requests/interfaces/DiscountCode";
import { PrimaryButton } from "../components/buttons/PrimaryButton";
import { DeleteButton } from "../components/buttons/DeleteButton";
import { SpinnerContainer } from "../components/common/SpinnerContainer";
import { addNotification, NotificationType } from "../store/features/notificationsSlice";
import { useAppDispatch } from "../store/hooks";
import { AdvertListModel } from "../api/requests/interfaces/Advert";
import { addAdvert, listAdverts } from "../api/requests/advert";
import { addHours, format } from "date-fns";
import { et } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { deleteBackground, deleteBackgroundPic, getBackground, initializeBackground, updateBackground } from "../api/requests/background";
import { BackgroundModel } from "../api/requests/interfaces/Background";
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Switch } from "@mui/material";
import api from "../api/apiInstance";
import { AxiosRequestConfig } from "axios";
import Popup from "../components/common/Popup";

export const AdminPage: React.FC<any> = () => {
    const [discountCodes, setDiscountCodes] = useState<DiscountCodeModel[]>([]);
    const [adverts, setAdverts] = useState<AdvertListModel[]>([]);
    const [background, setBackground] = useState<BackgroundModel | null>(null);
    const [isUpdatingCodes, setIsUpdatingCodes] = useState(false);
    const [isUpdatingBackground, setIsUpdatingBackground] = useState(false);
    const [isUploadingBackgroundPic, setIsUploadingBackgroundPic] = useState(false);
    const [backgroundExists, setBackgroundExists] = useState(false);
    const [showDeleteBackgroundPopup, setShowDeleteBackgroundPopup] = useState(false);
    const imageUploadRef = useRef<HTMLInputElement>(null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    
    useEffect(() => {
        fetchDiscountCodes();
        fetchAdverts();
        fetchBackground();
    }, [])

    const fetchDiscountCodes = async () => {
        const res = await getDiscountCodes();
        setDiscountCodes(res);
    }

    const fetchAdverts = async () => {
        const res = await listAdverts();
        setAdverts(res);
    }

    const fetchBackground = async () => {
        try {
            const res = await getBackground();
            setBackground(res);
            setBackgroundExists(true);
        } catch (error) {
            // Background doesn't exist yet
            setBackgroundExists(false);
        }
    }

    const handleInitializeBackground = async () => {
        try {
            await initializeBackground({
                name: 'Main Background',
                imageUrl: '',
                imageKey: '',
                isActive: true,
                imageOpacity: 'opacity-30',
                showDecorativeElements: true
            });
            fetchBackground();
            dispatch(addNotification({ message: "Taust edukalt lisatud", type: NotificationType.SUCCESS }));
        } catch (error) {
            dispatch(addNotification({ message: "Tausta lisamisel esines viga", type: NotificationType.ERROR }));
        }
    }

    const handleUpdateBackground = async () => {
        if (background) {
            setIsUpdatingBackground(true);
            try {
                await updateBackground({
                    name: background.name,
                    isActive: background.isActive,
                    imageOpacity: background.imageOpacity,
                    showDecorativeElements: background.showDecorativeElements
                });
                dispatch(addNotification({ message: "Taust edukalt uuendatud", type: NotificationType.SUCCESS }));
            } catch (error) {
                dispatch(addNotification({ message: "Tausta uuendamisel esines viga", type: NotificationType.ERROR }));
            }
            setIsUpdatingBackground(false);
        }
    }

    const handleBackgroundUpload = async (files: FileList | null) => {
        if (files === null || files?.item(0) === null) {
            return;
        }
        setIsUploadingBackgroundPic(true);
        try {
            const config: AxiosRequestConfig = {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
            var data = new FormData();
            data.append('file', files[0]);
            const res = await api.post<string>(process.env.REACT_APP_RENTIC_API_URL! + "/files/upload_background_picture", data, config);
            await fetchBackground();
            setIsUploadingBackgroundPic(false);
            dispatch(addNotification({ message: "Tausta pilt edukalt uuendatud", type: NotificationType.SUCCESS }));
        } catch (error) {
            setIsUploadingBackgroundPic(false);
            dispatch(addNotification({ message: "Tausta pildi uuendamisel esines viga", type: NotificationType.ERROR }));
        }
    }

    const handleDeleteBackground = async () => {
        try {
            // First delete the image file if it exists
            if (background?.imageUrl && background.imageUrl.length > 0) {
                await deleteBackgroundPic();
            }
            // Then delete the entity
            await deleteBackground();
            setShowDeleteBackgroundPopup(false);
            setBackgroundExists(false);
            setBackground(null);
            dispatch(addNotification({ message: "Taust edukalt kustutatud", type: NotificationType.SUCCESS }));
        } catch (error) {
            dispatch(addNotification({ message: "Tausta kustutamisel esines viga", type: NotificationType.ERROR }));
        }
    }

    const handleAddDiscountCode = async () => {
        await createDiscountCode({
            code: "",
            discountPercentage: 0,
            appliesToBoost: false
        });
        fetchDiscountCodes();
    }

    const handleAddAdvert = async () => {
        await addAdvert({name: 'Uus', pictureKey: '', pictureUrl: '', isActive: false});
        fetchAdverts();
    }

    const handleSaveDiscountCodeData = async (discountCodeId: number, codeIndx: number) => {
        setIsUpdatingCodes(true);
        await editDiscountCode(discountCodeId, discountCodes[codeIndx]);
        fetchDiscountCodes();
        setIsUpdatingCodes(false);
        dispatch(addNotification({message: "Kood uuendatud edukalt", type: NotificationType.SUCCESS}));
    }

    const handleDeleteDiscountCodeId = async (discountCodeId: number) => {
        await deleteDiscountCode(discountCodeId);
        fetchDiscountCodes();
    }

    const updateDiscountCodeItem = (id: number, newData: DiscountCodeModel) => {
        setDiscountCodes((prevCodes) => 
            prevCodes.map((itm) => itm.id === id ? {...itm, ...newData} : itm)
        );
    }

    return (
        <div>
            <h1 className="text-2xl mb-10">Administraatori leht</h1>
            <span className="text-xl">Sooduskoodid</span>
            <div className="overflow-x-auto w-full">
                <SpinnerContainer show={isUpdatingCodes}>
                {/* Header Row */}
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] border-b font-bold mt-2">
                    <span className="min-w-[200px]">Kood</span>
                    <span className="min-w-[100px]">Protsent</span>
                    <span className="min-w-[100px]">Rakendub esiletõstmishinnale</span>
                    <span className="min-w-[100px]">Kasutmiste arv</span>
                    <span className="min-w-[100px]"></span> {/* Save Button */}
                    <span className="min-w-[100px]"></span> {/* Delete Button */}
                </div>
                {/* Data Rows */}
                {discountCodes.map((itm, inx) => (
                    <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] items-center py-2" key={itm.id}>
                        <div className="min-w-[200px]">
                            <Input value={itm.code} placeHolder="Kood" onChange={(v) => updateDiscountCodeItem(itm.id, {...itm, code: v})} invalid={false} />
                        </div>
                        <div className="min-w-[100px]">
                            <NumberInput placeHolder="Soodus protsent" onChange={(v) => updateDiscountCodeItem(itm.id, {...itm, discountPercentage: v})} val={itm.discountPercentage} minVal={0.00} />
                        </div>
                        <div className="min-w-[100px]">
                            <Checkbox checked={itm.appliesToBoost} onChange={(v) => updateDiscountCodeItem(itm.id, {...itm, appliesToBoost: v})} />
                        </div>
                        <div className="min-w-[100px]">
                            <span>{itm.usedCount}</span>
                        </div>
                        <div className="pr-2 min-w-[100px]"><PrimaryButton title="Salvesta" onClick={() => handleSaveDiscountCodeData(itm.id, inx)} /> </div>
                        <div className="min-w-[100px]"><DeleteButton title="Kustuta" onClick={() => handleDeleteDiscountCodeId(itm.id)} /></div>
                    </div>
                ))}
                </SpinnerContainer>
            </div>
            <PrimaryButton title="Lisa sooduskood" onClick={() => handleAddDiscountCode()} widthFit={true}/>
            <div className="mt-10">
                <span className="text-xl">Reklaamid</span>
                <div className="overflow-x-auto w-full">
                    <div className="grid grid-cols-[2fr_1fr_1fr_1fr] border-b font-bold mt-2">
                        <span className="min-w-[200px]">Nimi</span>
                        <span className="min-w-[100px]">Aktiivne</span>
                        <span className="min-w-[100px]">Lisatud</span>
                        <span className="min-w-[100px]"></span>
                    </div>
                    {adverts.map((itm, inx) => (
                        <div className="grid grid-cols-[2fr_1fr_1fr_1fr] border-b mt-2 text-start">
                            <span className="min-w-[200px]">{itm.name}</span>
                            <span className="min-w-[100px] flex justify-center pt-2">
                                {
                                    itm.isActive ?
                                        <div className="rounded-full h-2 w-2 bg-green-500"></div>
                                    :
                                        <div className="rounded-full h-2 w-2 bg-red-500"></div>
                                }
                            </span>
                            <span className="min-w-[100px]">{format(addHours(itm.createdAt, 2), 'dd. MMM HH:mm, yyyy', {locale: et})}</span>
                            <span className="min-w-[100px]"><PrimaryButton title="Halda" onClick={() => {navigate('/admin/advert/' + itm.id.toString())}} /></span>
                        </div>
                    ))}
                </div>
            </div>
            <PrimaryButton title="Lisa reklaam" onClick={() => handleAddAdvert()} widthFit={true}/>
            
            {/* Background Section */}
            <div className="mt-10">
                <span className="text-xl">Taustapilt</span>
                {!backgroundExists ? (
                    <div className="mt-4">
                        <p className="mb-2">Tausta pole veel lisatud</p>
                        <PrimaryButton title="Lisa taust" onClick={() => handleInitializeBackground()} widthFit={true}/>
                    </div>
                ) : (
                    background && (
                        <SpinnerContainer show={isUpdatingBackground} fullWidth={true}>
                            <div className="flex flex-col sm:flex-row pt-2 gap-3 mt-4">
                                <div className="basis-full sm:basis-2/5 relative">
                                    <SpinnerContainer show={isUploadingBackgroundPic} fullWidth={true}>
                                        {
                                            background.imageUrl.length ?
                                            <img src={background.imageUrl} className="w-full object-cover rounded-lg h-[300px]"/>
                                            :
                                            <div className="w-full h-[300px] flex justify-center items-center font-bold bg-gray-100 rounded-lg">Pilt puudub</div>
                                        }
                                        <div className="absolute right-1 bottom-1 bg-gray-200 rounded-lg flex flex-row items-center p-1 cursor-pointer">
                                            <FileUploadOutlinedIcon style={{width: "20px", height: "20px"}} className=""/>
                                            <div onClick={() => imageUploadRef.current?.click()}>Muuda</div>
                                            <input type="file" id="upload_background_pic" name="filename" className='hidden'
                                                accept="image/png,image/jpeg,image/jpg,image/heif,image/webp" 
                                                ref={imageUploadRef} 
                                                onChange={(e) => { handleBackgroundUpload(e.target.files); e.target.files = null; }} />
                                        </div>
                                    </SpinnerContainer>
                                </div>
                                <div className="basis-full sm:basis-3/5">
                                    <h4 className="text-left">Nimi</h4>
                                    <Input value={background.name} placeHolder="Tausta nimi" onChange={(v) => {setBackground({...background, name: v})}} />
                                    
                                    {/* <h4 className="text-left pt-2">Kirjeldus</h4>
                                    <Input value={background.description ?? ""} placeHolder="Kirjeldus" onChange={(v) => {setBackground({...background, description: v})}} />
                                    
                                    <div className="flex flex-col justify-start pt-2">
                                        <h4 className="text-left">Aktiivne</h4>
                                        <Switch
                                            checked={background.isActive}
                                            onChange={(v, ch) => {setBackground({...background, isActive: ch})}}
                                        />
                                    </div>
                                    
                                    <div className="flex flex-col justify-start pt-2">
                                        <h4 className="text-left">Näita dekoratiivseid elemente</h4>
                                        <Switch
                                            checked={background.showDecorativeElements}
                                            onChange={(v, ch) => {setBackground({...background, showDecorativeElements: ch})}}
                                        />
                                    </div> */}
                                    
                                    <div className="flex flex-col justify-start pt-2">
                                        <h4 className="text-left">Läbipaistvus (opacity)</h4>
                                        <select 
                                            value={background.imageOpacity} 
                                            onChange={(e) => setBackground({...background, imageOpacity: e.target.value})}
                                            className="border border-gray-300 rounded-md p-2"
                                        >
                                            <option value="opacity-10">10%</option>
                                            <option value="opacity-20">20%</option>
                                            <option value="opacity-30">30%</option>
                                            <option value="opacity-40">40%</option>
                                            <option value="opacity-50">50%</option>
                                            <option value="opacity-60">60%</option>
                                            <option value="opacity-70">70%</option>
                                            <option value="opacity-80">80%</option>
                                            <option value="opacity-90">90%</option>
                                            <option value="opacity-100">100%</option>
                                        </select>
                                    </div>
                                    
                                    <div className="flex flex-col justify-start pt-2">
                                        <h4 className="text-left">Viimati uuendatud</h4>
                                        <span className="text-left">{format(addHours(background.updatedAt, 2), 'dd. MMM, HH:mm yyyy', {locale: et})}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-1 mt-2">
                                <PrimaryButton title="Salvesta" onClick={() => handleUpdateBackground()}></PrimaryButton>
                                <DeleteButton title="Kustuta taust" onClick={() => setShowDeleteBackgroundPopup(true)}></DeleteButton>
                            </div>
                        </SpinnerContainer>
                    )
                )}
            </div>
            
            {/* Delete Background Popup */}
            {showDeleteBackgroundPopup &&
                <Popup
                    message="Kas oled kindel, et kustutada taust? See kustutab nii pildi kui ka kõik seaded."
                    onConfirm={() => {handleDeleteBackground()}}
                    onCancel={() => {setShowDeleteBackgroundPopup(false)}}
                />
            }
        </div>
    )
}